// views/Geradoras/interrupcoes.ts
//
// Lógica pura do registro de interrupções das usinas (solicitação #6237): a
// lista de motivos, o casamento entre o que a telemetria detectou e o que uma
// pessoa registrou, os totais por usina e a decomposição da diferença entre
// usinas irmãs (#6216). Sem React e sem Supabase de propósito — é o pedaço
// que erra silencioso se não for testado (node --test).
//
// A DETECÇÃO não mora aqui: é a RPC get_interrupcoes_geradoras
// (migration 20261011120000), que roda no Postgres em cima do acumulador.

import type { InterrupcaoDetectada, RegistroInterrupcao, TipoInterrupcao } from '../../types';
import { getConjuntoUsina } from './geradorasShared.ts';

// ---------------- Tipos de episódio ----------------

export interface EstiloTipo {
    rotulo: string;
    /** Uma frase para quem nunca viu a tela. */
    explicacao: string;
    /** Conta como geração perdida? Falta de dado não é perda. */
    ehPerda: boolean;
}

export const TIPO_INTERRUPCAO: Record<TipoInterrupcao, EstiloTipo> = {
    parada: {
        rotulo: 'Parada',
        explicacao: 'A usina não gerou: o acumulador ficou parado e a energia não apareceu depois.',
        ehPerda: true,
    },
    parcial: {
        rotulo: 'Operação parcial',
        explicacao: 'Gerou menos de 85% das usinas irmãs. É o retrato de inversor fora.',
        ehPerda: true,
    },
    sem_comunicacao: {
        rotulo: 'Sem comunicação',
        explicacao: 'Ficou sem mandar dado, mas gerou: a energia apareceu no acumulador quando o dado voltou.',
        ehPerda: false,
    },
    sem_leitura: {
        rotulo: 'Sem leitura',
        explicacao: 'O nosso coletor não gravou. É problema de integração, não da usina.',
        ehPerda: false,
    },
};

// ---------------- Motivos ----------------

export interface MotivoInterrupcao {
    id: string;
    rotulo: string;
    /**
     * false = quem classificou está dizendo que aquilo NÃO foi geração perdida
     * (cadastro errado, tempo fechado só ali). O episódio continua na lista,
     * mas sai dos totais.
     */
    contaComoPerda: boolean;
}

/**
 * Lista fechada na tela, texto livre no banco: motivo novo entra aqui sem
 * migration. Não reaproveite um `id` com outro sentido — os registros antigos
 * guardam o id, não o rótulo.
 */
export const MOTIVOS_INTERRUPCAO: readonly MotivoInterrupcao[] = [
    { id: 'falha_inversor', rotulo: 'Falha de inversor', contaComoPerda: true },
    { id: 'desligamento_distribuidora', rotulo: 'Desligamento da distribuidora', contaComoPerda: true },
    { id: 'oscilacao_rede', rotulo: 'Oscilação da rede (tensão ou frequência)', contaComoPerda: true },
    { id: 'manutencao_programada', rotulo: 'Manutenção programada', contaComoPerda: true },
    { id: 'manutencao_corretiva', rotulo: 'Manutenção corretiva', contaComoPerda: true },
    { id: 'falha_comunicacao', rotulo: 'Falha de comunicação da usina', contaComoPerda: true },
    { id: 'falha_coleta', rotulo: 'Falha da nossa coleta (TI)', contaComoPerda: true },
    { id: 'clima', rotulo: 'Tempo fechado só nesta usina (não é falha)', contaComoPerda: false },
    { id: 'cadastro', rotulo: 'Cadastro ou característica da usina (não é interrupção)', contaComoPerda: false },
    { id: 'outro', rotulo: 'Outro', contaComoPerda: true },
];

const MOTIVO_POR_ID = new Map(MOTIVOS_INTERRUPCAO.map(m => [m.id, m]));

/** Motivo desconhecido (id antigo, digitado direto no banco) aparece como veio e conta como perda. */
export const getMotivo = (id: string | null | undefined): MotivoInterrupcao | null => {
    if (!id) return null;
    return MOTIVO_POR_ID.get(id) ?? { id, rotulo: id, contaComoPerda: true };
};

// ---------------- Casamento: detectado x registrado ----------------

/**
 * Uma linha da lista da tela. Vem da telemetria (com ou sem motivo
 * registrado) ou só de um registro manual.
 */
export interface LinhaInterrupcao {
    /** Estável entre consultas: serve de key do React e de âncora do motivo. */
    chave: string;
    site_id_api: string;
    tipo: TipoInterrupcao;
    inicio: string;
    /** null = registro manual sem fim informado. */
    fim: string | null;
    /** Null quando a linha é só um registro manual. */
    detectada: InterrupcaoDetectada | null;
    registro: RegistroInterrupcao | null;
}

const UMA_HORA = 3600000;

/**
 * O registro de classificação pertence a este episódio?
 *
 * A classificação guarda o INÍCIO do episódio no momento em que foi feita, e a
 * detecção é determinística para o passado — então, no caso normal, os dois
 * inícios são o mesmo instante. A folga de 1 h cobre só arredondamento.
 *
 * Episódio que já vinha de antes da janela analisada tem o início truncado
 * (anda junto com o período na tela), e aí o início guardado fica ANTES do
 * detectado: vale o registro que começou antes e não terminou antes.
 */
const classificacaoCasa = (r: RegistroInterrupcao, e: InterrupcaoDetectada): boolean => {
    if (r.origem !== 'classificacao' || r.site_id_api !== e.site_id_api || r.tipo !== e.tipo) return false;
    const rIni = Date.parse(r.inicio);
    const eIni = Date.parse(e.inicio);
    const eFim = Date.parse(e.fim);
    if (rIni >= eIni - UMA_HORA && rIni < eFim) return true;
    if (!e.continua_de_antes) return false;
    return rIni < eIni && (r.fim == null || Date.parse(r.fim) > eIni);
};

/**
 * Junta o que a telemetria detectou com o que as pessoas registraram.
 *
 * - Cada episódio detectado ganha, no máximo, UMA classificação: a mais
 *   recente que casa (quem reclassifica está corrigindo a anterior).
 * - Registro manual vira linha própria — é, por definição, o que o sistema
 *   não enxergou — desde que toque o período.
 * - Classificação órfã (o episódio deixou de ser detectado, por exemplo
 *   depois de um ajuste de regra) NÃO vira linha: ela descreve um episódio
 *   que a tela não mostra mais, e sozinha confundiria.
 *
 * Saída do mais recente para o mais antigo.
 */
export const casarRegistros = (
    detectadas: InterrupcaoDetectada[],
    registros: RegistroInterrupcao[],
    periodo: { inicio: Date; fim: Date },
): LinhaInterrupcao[] => {
    const maisRecentePrimeiro = [...registros].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));

    const linhas: LinhaInterrupcao[] = detectadas.map(e => ({
        chave: `auto|${e.site_id_api}|${e.tipo}|${e.inicio}`,
        site_id_api: e.site_id_api,
        tipo: e.tipo,
        inicio: e.inicio,
        fim: e.fim,
        detectada: e,
        registro: maisRecentePrimeiro.find(r => classificacaoCasa(r, e)) ?? null,
    }));

    const pIni = periodo.inicio.getTime();
    const pFim = periodo.fim.getTime();
    registros
        .filter(r => r.origem === 'manual')
        .filter(r => Date.parse(r.inicio) < pFim && (r.fim == null || Date.parse(r.fim) > pIni))
        .forEach(r => linhas.push({
            chave: `manual|${r.id}`,
            site_id_api: r.site_id_api,
            tipo: r.tipo,
            inicio: r.inicio,
            fim: r.fim,
            detectada: null,
            registro: r,
        }));

    return linhas.sort((a, b) => Date.parse(b.inicio) - Date.parse(a.inicio));
};

/** A linha entra nos totais de perda? Tipo que é perda E motivo que não a desmente. */
export const contaComoPerda = (linha: LinhaInterrupcao): boolean => {
    if (!TIPO_INTERRUPCAO[linha.tipo].ehPerda) return false;
    return getMotivo(linha.registro?.motivo)?.contaComoPerda ?? true;
};

// ---------------- Totais ----------------

export interface ResumoInterrupcoesUsina {
    site_id_api: string;
    /** Episódios de parada que contam como perda. */
    paradas: number;
    horasParada: number;
    /** Dias em operação parcial, dentro do período. */
    diasParcial: number;
    /** Horas de parada total equivalentes: soma parada + parcial. */
    horasEquivalentes: number;
    /** kWh não gerados (estimativa), dentro do período. */
    energiaNaoGeradaKwh: number;
    /** Algum número veio da referência 'frota' (outro local): estimativa mais solta. */
    temReferenciaFrota: boolean;
    /** Episódios sem dado (comunicação ou leitura): não são perda, mas tiram visibilidade. */
    semDado: number;
    horasSemDado: number;
    /** Episódios que a pessoa marcou como "não é perda". */
    desconsiderados: number;
    semMotivo: number;
}

const vazio = (site: string): ResumoInterrupcoesUsina => ({
    site_id_api: site, paradas: 0, horasParada: 0, diasParcial: 0, horasEquivalentes: 0,
    energiaNaoGeradaKwh: 0, temReferenciaFrota: false, semDado: 0, horasSemDado: 0,
    desconsiderados: 0, semMotivo: 0,
});

/**
 * Totais por usina, só com a parte de cada episódio que cai DENTRO do período
 * (colunas `_periodo` da RPC): um episódio de 15 dias que começou no mês
 * passado não pode despejar os 15 dias no mês corrente.
 *
 * Registro manual não tem medição — entra na contagem de episódios, não nas
 * horas nem na energia.
 */
export const resumirPorUsina = (linhas: LinhaInterrupcao[]): ResumoInterrupcoesUsina[] => {
    const porSite = new Map<string, ResumoInterrupcoesUsina>();
    const de = (site: string) => {
        let r = porSite.get(site);
        if (!r) { r = vazio(site); porSite.set(site, r); }
        return r;
    };

    linhas.forEach(l => {
        const r = de(l.site_id_api);
        const e = l.detectada;
        if (!TIPO_INTERRUPCAO[l.tipo].ehPerda) {
            r.semDado += 1;
            r.horasSemDado += e?.horas_sol_periodo ?? 0;
            return;
        }
        if (!contaComoPerda(l)) { r.desconsiderados += 1; return; }
        if (!l.registro) r.semMotivo += 1;
        if (l.tipo === 'parada') {
            r.paradas += 1;
            r.horasParada += e?.horas_sol_periodo ?? 0;
        }
        if (!e) return;
        if (l.tipo === 'parcial' && e.dias != null && e.horas_sol > 0) {
            // dias dentro do período, na mesma proporção das horas de sol
            r.diasParcial += e.dias * (e.horas_sol_periodo / e.horas_sol);
        }
        r.horasEquivalentes += e.horas_equivalentes_periodo ?? 0;
        r.energiaNaoGeradaKwh += e.energia_nao_gerada_kwh_periodo ?? 0;
        if (e.referencia === 'frota' && (e.energia_nao_gerada_kwh_periodo ?? 0) > 0) r.temReferenciaFrota = true;
    });

    return [...porSite.values()]
        .map(r => ({ ...r, diasParcial: Math.round(r.diasParcial) }))
        .sort((a, b) => b.energiaNaoGeradaKwh - a.energiaNaoGeradaKwh || b.horasSemDado - a.horasSemDado);
};

// ---------------- Por que gerou menos (#6216) ----------------
//
// A #6216 pedia para relacionar ALARME com geração abaixo do esperado. A
// medição (24/08 a 10/10/2026) mostrou que alarme sozinho não separa dia bom
// de dia ruim: nos Blocos há alarme em 32% dos dias normais e em 42% dos dias
// com perda; na Qgbt1 e na Qgbt2, em 86% dos dias normais. O que explica a
// diferença entre irmãs é a INTERRUPÇÃO. Daí esta conta: de quanto a usina
// ficou abaixo da melhor irmã, quanto as interrupções explicam e quanto sobra.

/**
 * Acima disto, em kWh por kWp num único dia, não é geração: é o acumulador
 * que saltou (ressincronizou o total, ou despejou vários dias de uma vez).
 * Usina boa em dia limpo faz 6 a 7; a Qgbt1 marcou 94 num dia de set/2026.
 */
const LIMITE_KWH_KWP_DIA = 12;

/** Geração de uma usina no período — o que a aba Análise já carrega para o ranking. */
export interface GeracaoUsinaPeriodo {
    site_id_api: string;
    nome_usina: string;
    /** MWp, como em unidades_geradoras.capacidade_instalada. */
    capacidade_instalada: number | null;
    geracao_kwh: number | null;
    melhor_dia_kwh: number | null;
    dias_com_dado: number;
}

/**
 * Por que a usina ficou fora da comparação:
 *   avulsa          não faz parte de conjunto — não tem irmã
 *   sem_irmas       o conjunto não tem outra usina com dado comparável
 *   sem_capacidade  sem MWp cadastrado, não dá para pôr na mesma régua
 *   sem_geracao     sem geração registrada no período
 *   acumulador      o acumulador saltou no período: o total dela não é confiável
 */
export type MotivoSemComparacao = 'avulsa' | 'sem_irmas' | 'sem_capacidade' | 'sem_geracao' | 'acumulador';

export interface ComparacaoComIrmas {
    /** É ela a melhor do conjunto: a régua das outras. */
    ehReferencia: boolean;
    referenciaSite: string;
    /** O que ela teria gerado a mais com o rendimento (kWh/kWp) da melhor irmã. */
    abaixoKwh: number;
    abaixoPct: number;
    /** Parte da diferença que as interrupções explicam. */
    explicadoKwh: number;
    /** Parte que nenhuma interrupção explica: rendimento. */
    sobraKwh: number;
    sobraPct: number;
}

export interface LinhaDecomposicao {
    site_id_api: string;
    conjunto: { id: string; titulo: string } | null;
    geracaoKwh: number | null;
    comparacao: ComparacaoComIrmas | null;
    semComparacao: MotivoSemComparacao | null;
    /** Dias de dado a menos que a referência: parte da diferença pode ser leitura faltando. */
    diasSemDado: number;
}

/**
 * Para cada usina de conjunto: quanto ficou abaixo da melhor irmã no período,
 * quanto disso as interrupções explicam e quanto sobra.
 *
 * - A régua é kWh/kWp, e a referência é a MELHOR irmã com dado confiável (não
 *   a mediana: com duas de cinco usinas em problema, a mediana já vem
 *   rebaixada e esconde a perda).
 * - "Explicado" é a perda em interrupções ALÉM da que a própria referência
 *   teve. Parada que derruba o conjunto inteiro (Morrinhos, 06-07/10/2026)
 *   tira geração de todas por igual: é perda, mas não é diferença entre irmãs,
 *   e não pode "explicar" diferença nenhuma.
 * - Nunca explica mais do que a diferença: a perda é estimativa e pode passar.
 *
 * ⚠️ kWh/kWp depende da capacidade cadastrada. Cadastro errado aparece aqui
 * como "sobra sem explicação" — é o caso em aberto da Qgbt2 (1,1 MWp contra
 * 1,0 das irmãs).
 */
export const decomporDiferenca = (
    usinas: GeracaoUsinaPeriodo[],
    resumo: Pick<ResumoInterrupcoesUsina, 'site_id_api' | 'energiaNaoGeradaKwh'>[],
): LinhaDecomposicao[] => {
    const perda = new Map(resumo.map(r => [r.site_id_api, r.energiaNaoGeradaKwh]));

    interface Elegivel { u: GeracaoUsinaPeriodo; kwp: number; kwhKwp: number }
    const elegiveis = new Map<string, Elegivel[]>();
    const base: LinhaDecomposicao[] = usinas.map(u => {
        const conjunto = getConjuntoUsina(u.nome_usina);
        const linha: LinhaDecomposicao = {
            site_id_api: u.site_id_api,
            conjunto: conjunto ? { id: conjunto.id, titulo: conjunto.titulo } : null,
            geracaoKwh: u.geracao_kwh,
            comparacao: null,
            semComparacao: null,
            diasSemDado: 0,
        };
        const kwp = u.capacidade_instalada && u.capacidade_instalada > 0 ? u.capacidade_instalada * 1000 : null;
        if (!conjunto) linha.semComparacao = 'avulsa';
        else if (!kwp) linha.semComparacao = 'sem_capacidade';
        else if (u.geracao_kwh == null) linha.semComparacao = 'sem_geracao';
        else if (u.melhor_dia_kwh != null && u.melhor_dia_kwh / kwp > LIMITE_KWH_KWP_DIA) linha.semComparacao = 'acumulador';
        else {
            const lista = elegiveis.get(conjunto.id) || [];
            lista.push({ u, kwp, kwhKwp: u.geracao_kwh / kwp });
            elegiveis.set(conjunto.id, lista);
        }
        return linha;
    });

    const porSite = new Map(base.map(l => [l.site_id_api, l]));
    elegiveis.forEach(lista => {
        if (lista.length < 2) {
            lista.forEach(e => { porSite.get(e.u.site_id_api)!.semComparacao = 'sem_irmas'; });
            return;
        }
        // Empate desempata pelo nome, para a referência não trocar entre uma consulta e outra.
        const ref = [...lista].sort((a, b) => b.kwhKwp - a.kwhKwp || a.u.nome_usina.localeCompare(b.u.nome_usina, 'pt-BR'))[0];
        const perdaRefPorKwp = (perda.get(ref.u.site_id_api) || 0) / ref.kwp;
        lista.forEach(e => {
            const potencial = ref.kwhKwp * e.kwp;
            const abaixoKwh = Math.max(potencial - (e.u.geracao_kwh as number), 0);
            const perdaAlemDaReferencia = Math.max((perda.get(e.u.site_id_api) || 0) - perdaRefPorKwp * e.kwp, 0);
            const explicadoKwh = Math.min(perdaAlemDaReferencia, abaixoKwh);
            const sobraKwh = abaixoKwh - explicadoKwh;
            const linha = porSite.get(e.u.site_id_api)!;
            linha.diasSemDado = Math.max(ref.u.dias_com_dado - e.u.dias_com_dado, 0);
            linha.comparacao = {
                ehReferencia: e === ref,
                referenciaSite: ref.u.site_id_api,
                abaixoKwh,
                abaixoPct: potencial > 0 ? (abaixoKwh / potencial) * 100 : 0,
                explicadoKwh,
                sobraKwh,
                sobraPct: potencial > 0 ? (sobraKwh / potencial) * 100 : 0,
            };
        });
    });

    // Conjunto a conjunto, de quem ficou mais abaixo para quem ficou menos;
    // usina fora de comparação fecha o conjunto dela; as avulsas vão ao fim.
    return base.sort((a, b) =>
        (a.conjunto ? 0 : 1) - (b.conjunto ? 0 : 1)
        || (a.conjunto?.titulo || '').localeCompare(b.conjunto?.titulo || '', 'pt-BR')
        || (a.comparacao ? 0 : 1) - (b.comparacao ? 0 : 1)
        || (b.comparacao?.abaixoKwh ?? 0) - (a.comparacao?.abaixoKwh ?? 0)
        || a.site_id_api.localeCompare(b.site_id_api));
};

// ---------------- Texto ----------------

const FUSO = 'America/Sao_Paulo';
const DIA_HORA = new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit', hour: '2-digit', hourCycle: 'h23' });
const SO_DIA = new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit' });

/** "06/10 às 08h" — a detecção é por hora cheia, então minuto seria precisão falsa. */
export const rotuloInstante = (iso: string): string => {
    const partes = DIA_HORA.formatToParts(new Date(iso));
    const p = (t: string) => partes.find(x => x.type === t)?.value ?? '';
    return `${p('day')}/${p('month')} às ${p('hour')}h`;
};

/**
 * Período do episódio em texto. Operação parcial é medida por dia, então sai
 * só com as datas ("10/09 a 24/09"); o resto sai com a hora.
 */
export const rotuloPeriodo = (linha: Pick<LinhaInterrupcao, 'tipo' | 'inicio' | 'fim'>): string => {
    if (linha.fim == null) return `desde ${rotuloInstante(linha.inicio)}`;
    if (linha.tipo === 'parcial') {
        const a = SO_DIA.format(new Date(linha.inicio));
        // o fim é exclusivo (fim da última hora de sol): recua 1 min para cair no dia certo
        const b = SO_DIA.format(new Date(Date.parse(linha.fim) - 60000));
        return a === b ? a : `${a} a ${b}`;
    }
    return `${rotuloInstante(linha.inicio)} → ${rotuloInstante(linha.fim)}`;
};

/** "15 h de sol" | "2 dias" — a duração na unidade em que o tipo é medido. */
export const rotuloDuracao = (e: InterrupcaoDetectada): string => {
    if (e.tipo === 'parcial' && e.dias != null) return e.dias === 1 ? '1 dia' : `${e.dias} dias`;
    const h = Math.round(e.horas_sol);
    return h === 1 ? '1 h de sol' : `${h.toLocaleString('pt-BR')} h de sol`;
};

/** Horas com uma casa só quando faz diferença: "0,9 h", "15 h", "1.240 h". */
export const formatHoras = (horas: number | null | undefined): string => {
    if (horas == null) return '—';
    const casas = horas > 0 && horas < 10 && Math.abs(horas - Math.round(horas)) >= 0.05 ? 1 : 0;
    return `${horas.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })} h`;
};
